import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { Button, Field, Notice } from '../../design-system/components';

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
const get = (obj: any, path: string) => path.split('.').reduce((v, k) => v?.[k], obj);
const setPath = (obj: any, path: string, value: any) => {
  const next = clone(obj || {});
  const keys = path.split('.');
  let cursor = next;
  keys.slice(0, -1).forEach((k) => {
    cursor[k] = cursor[k] && typeof cursor[k] === 'object' ? cursor[k] : {};
    cursor = cursor[k];
  });
  cursor[keys.at(-1)!] = value;
  return next;
};
const pct = (v: any) => String(Number(v || 0) * 100).replace('.', ',');
const ratio = (v: string) => String((Number(v.replace(',', '.')) / 100).toFixed(6));

export function GrossupProfileSettings() {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [form, setForm] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [duplicateId, setDuplicateId] = useState('');
  async function load(preferred?: string) {
    try {
      const rows = await api.get<any[]>('/grossup/profiles');
      setProfiles(rows);
      const key = preferred || selectedKey || `${rows[0]?.profile_id}@${rows[0]?.version}`;
      const row = rows.find((x) => `${x.profile_id}@${x.version}` === key) || rows[0];
      if (row) {
        setSelectedKey(`${row.profile_id}@${row.version}`);
        setForm(clone(row.parameters));
        setHistory(await api.get<any[]>(`/grossup/profiles/${row.profile_id}/history`));
      }
    } catch (c: any) {
      setError(c.message);
    }
  }
  useEffect(() => {
    load();
  }, []);
  async function choose(key: string) {
    setSelectedKey(key);
    const row = profiles.find((x) => `${x.profile_id}@${x.version}` === key);
    if (row) {
      setForm(clone(row.parameters));
      setHistory(await api.get<any[]>(`/grossup/profiles/${row.profile_id}/history`));
    }
  }
  function field(path: string, value: any) {
    setForm((old: any) => setPath(old, path, value));
  }
  async function save() {
    setError('');
    setMessage('');
    try {
      const response = await api.put<any>(`/grossup/profiles/${form.profile_id}`, { parameters: form });
      setMessage(`Nova versão ${response.version} salva sem alterar as anteriores.`);
      await load(`${response.profile_id}@${response.version}`);
    } catch (c: any) {
      setError(c.message);
    }
  }
  async function activate(active: boolean) {
    const [rowId, version] = selectedKey.split('@');
    try {
      await api.post(`/grossup/profiles/${rowId}/${version}/activation`, { active });
      setMessage(active ? 'Perfil ativado.' : 'Perfil desativado.');
      await load(selectedKey);
    } catch (c: any) {
      setError(c.message);
    }
  }
  async function duplicate() {
    const [rowId, version] = selectedKey.split('@');
    if (!duplicateId.trim()) {
      setError('Informe o código do novo perfil.');
      return;
    }
    try {
      const response = await api.post<any>(`/grossup/profiles/${rowId}/${version}/duplicate`, { new_profile_id: duplicateId.trim() });
      setMessage(`Perfil duplicado como ${response.profile_id}.`);
      setDuplicateId('');
      await load(`${response.profile_id}@${response.version}`);
    } catch (c: any) {
      setError(c.message);
    }
  }
  function exportJson() {
    const blob = new Blob([JSON.stringify(form, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.profile_id}_${form.version}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
  async function importJson(file: File) {
    try {
      const parameters = JSON.parse(await file.text());
      const response = await api.post<any>('/grossup/profiles/import', { parameters });
      setMessage(`Configuração importada como nova versão ${response.version}.`);
      await load(`${response.profile_id}@${response.version}`);
    } catch (c: any) {
      setError(c instanceof SyntaxError ? 'Arquivo JSON inválido.' : c.message);
    }
  }
  const row = profiles.find((x) => `${x.profile_id}@${x.version}` === selectedKey);
  const brackets = useMemo(() => form?.autonomous_provider?.irrf_table?.brackets || [], [form]);
  const technical = form?.technical_pricing || {};
  const technicalHour = useMemo(() => {
    const monthly = (technical?.office_costs || []).reduce((sum: number, item: any) => sum + Number(item.monthly_value || 0), 0);
    const available = Number(technical?.capacity?.available_hours_month || 0);
    const billable = Number(technical?.capacity?.billable_fraction || 0);
    const markup = Number(technical?.capacity?.hour_markup || 0);
    const billableHours = available * billable;
    const costHour = billableHours > 0 ? monthly / billableHours : 0;
    return { monthly, billableHours, costHour, saleHour: costHour * (1 + markup) };
  }, [technical]);
  if (!form) return <section className="settings-card">Carregando perfis...</section>;
  return (
    <section className="settings-card grossup-profile-settings">
      <header>
        <span className="eyebrow">PERFIS INTERNOS</span>
        <h2>Formação do preço bruto e parâmetros versionados</h2>
        <p>O perfil ativo é a fonte única consumida pelo motor técnico e financeiro. Cada salvamento cria uma nova versão sem alterar propostas emitidas.</p>
      </header>
      {message && (
        <Notice tone="success" title="Configuração atualizada">
          {message}
        </Notice>
      )}
      {error && (
        <Notice tone="danger" title="Não foi possível concluir">
          {error}
        </Notice>
      )}
      <div className="profile-toolbar">
        <Field id="profile-select" label="Perfil e versão">
          <select id="profile-select" value={selectedKey} onChange={(e) => choose(e.target.value)}>
            {profiles.map((x) => (
              <option key={`${x.profile_id}@${x.version}`} value={`${x.profile_id}@${x.version}`}>
                {x.profile_id} · {x.version} · {x.active ? 'ativo' : 'inativo'}
              </option>
            ))}
          </select>
        </Field>
        <div className="profile-actions">
          <Button type="button" onClick={save}>
            Salvar nova versão
          </Button>
          <Button type="button" variant="secondary" onClick={() => activate(!row?.active)}>
            {row?.active ? 'Desativar perfil' : 'Ativar perfil'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setShowHistory((v) => !v)}>
            Ver histórico
          </Button>
        </div>
      </div>
      <ProfileSection title="Identificação">
        <div className="form-grid four-columns">
          <Text path="profile_id" label="Código" value={form.profile_id} set={field} />
          <Text path="version" label="Versão solicitada" value={form.version} set={field} />
          <Text path="valid_from" label="Início de vigência" value={form.valid_from} set={field} type="date" />
          <Text path="valid_to" label="Fim de vigência" value={form.valid_to || ''} set={field} type="date" />
          <Text path="status" label="Status" value={form.status} set={field} />
          <Text path="validation_responsible" label="Responsável pela validação" value={form.validation_responsible || ''} set={field} />
          <Text path="observations" label="Observações" value={form.observations || ''} set={field} />
        </div>
      </ProfileSection>
      <ProfileSection title="Empresa">
        <div className="form-grid four-columns">
          <Text path="regime_simulado" label="Regime simulado" value={form.regime_simulado} set={field} />
          <Text path="anexo_simulado" label="Anexo" value={form.anexo_simulado} set={field} />
          <Percent path="company_tax_rate" label="Alíquota efetiva" value={form.company_tax_rate} set={field} />
          <Text path="municipality" label="Município" value={form.municipality || ''} set={field} />
        </div>
      </ProfileSection>
      <ProfileSection title="Divisão interna">
        <div className="form-grid">
          <Percent path="internal_split.0" label="Parte A" value={form.internal_split?.[0]} set={field} />
          <Percent path="internal_split.1" label="Parte B" value={form.internal_split?.[1]} set={field} />
          <div className="field">
            <span>Soma</span>
            <strong>{(Number(form.internal_split?.[0] || 0) + Number(form.internal_split?.[1] || 0)) * 100}%</strong>
          </div>
        </div>
      </ProfileSection>
      <ProfileSection title="INSS do prestador">
        <div className="form-grid four-columns">
          <Percent path="autonomous_provider.inss_rate" label="Alíquota" value={get(form, 'autonomous_provider.inss_rate')} set={field} />
          <Text path="autonomous_provider.inss_ceiling" label="Teto" value={get(form, 'autonomous_provider.inss_ceiling')} set={field} />
          <Text
            path="autonomous_provider.other_inss_bases"
            label="Bases já utilizadas"
            value={get(form, 'autonomous_provider.other_inss_bases')}
            set={field}
          />
          <Text
            path="autonomous_provider.valid_from"
            label="Vigência"
            value={get(form, 'autonomous_provider.valid_from')}
            set={field}
            type="date"
          />
        </div>
      </ProfileSection>
      <ProfileSection title="IRRF">
        <div className="form-grid four-columns">
          <Text
            path="autonomous_provider.irrf_table.table_id"
            label="Tabela mensal"
            value={get(form, 'autonomous_provider.irrf_table.table_id')}
            set={field}
          />
          <Text
            path="autonomous_provider.irrf_table.valid_from"
            label="Vigência"
            value={get(form, 'autonomous_provider.irrf_table.valid_from')}
            set={field}
            type="date"
          />
          <Text
            path="autonomous_provider.dependents"
            label="Dependentes"
            value={get(form, 'autonomous_provider.dependents')}
            set={field}
            type="number"
          />
          <Text
            path="autonomous_provider.other_deductions"
            label="Outras deduções"
            value={get(form, 'autonomous_provider.other_deductions')}
            set={field}
          />
        </div>
        <div className="bracket-table">
          <div className="bracket-head">
            <span>Limite superior</span>
            <span>Alíquota</span>
            <span>Parcela a deduzir</span>
          </div>
          {brackets.map((b: any, i: number) => (
            <div className="bracket-row" key={i}>
              <input
                aria-label={`Limite da faixa ${i + 1}`}
                value={b.upper ?? ''}
                placeholder="Sem limite"
                onChange={(e) => {
                  const next = clone(brackets);
                  next[i].upper = e.target.value || null;
                  field('autonomous_provider.irrf_table.brackets', next);
                }}
              />
              <input
                aria-label={`Alíquota da faixa ${i + 1}`}
                value={pct(b.rate)}
                onChange={(e) => {
                  const next = clone(brackets);
                  next[i].rate = ratio(e.target.value);
                  field('autonomous_provider.irrf_table.brackets', next);
                }}
              />
              <input
                aria-label={`Dedução da faixa ${i + 1}`}
                value={b.deduction}
                onChange={(e) => {
                  const next = clone(brackets);
                  next[i].deduction = e.target.value;
                  field('autonomous_provider.irrf_table.brackets', next);
                }}
              />
            </div>
          ))}
        </div>
      </ProfileSection>
      <ProfileSection title="Pró-labore, reserva e arredondamento">
        <div className="form-grid four-columns">
          <Text path="pro_labore_gross" label="Pró-labore bruto" value={form.pro_labore_gross} set={field} />
          <Percent path="pro_labore_inss_rate" label="INSS aplicável" value={form.pro_labore_inss_rate} set={field} />
          <Percent path="reserve_rate" label="Reserva" value={form.reserve_rate} set={field} />
          <Percent path="management_limit_rate" label="Limite" value={form.management_limit_rate} set={field} />
          <Text path="rounding_unit" label="Múltiplo" value={form.rounding_unit} set={field} />
          <label className="toggle-card">
            <input type="checkbox" checked={form.rounding_mode === 'ceil'} onChange={() => field('rounding_mode', 'ceil')} />
            <span>
              <strong>Arredondar para cima</strong>
              <small>Nunca reduzir o bruto calculado.</small>
            </span>
          </label>
          <label className="toggle-card">
            <input
              type="checkbox"
              checked={form.manual_adjustment_allowed !== false}
              onChange={(e) => field('manual_adjustment_allowed', e.target.checked)}
            />
            <span>
              <strong>Ajuste manual permitido</strong>
              <small>Com rastreabilidade.</small>
            </span>
          </label>
        </div>
      </ProfileSection>
      <ProfileSection title="Fonte ativa e hora técnica calculada">
        <Notice tone={row?.active ? 'success' : 'warning'} title={row?.active ? 'Versão ativa no motor' : 'Versão histórica ou inativa'}>
          {row?.active
            ? `Propostas novas adotarão ${form.profile_id} · ${form.version} e o snapshot técnico ${technical.version}.`
            : 'Esta versão permanece disponível para auditoria e propostas já vinculadas, mas não é adotada automaticamente por propostas novas.'}
        </Notice>
        <div className="form-grid four-columns">
          <Text path="technical_pricing.version" label="Versão técnica" value={technical.version} set={field} />
          <Text path="technical_pricing.classification" label="Classificação" value={technical.classification} set={field} />
          <ReadOnly label="Custo mensal total" value={money(technicalHour.monthly)} />
          <ReadOnly label="Horas faturáveis calculadas" value={technicalHour.billableHours.toFixed(2)} />
          <ReadOnly label="Custo da hora" value={money(technicalHour.costHour)} />
          <ReadOnly label="Hora técnica líquida da ZURC" value={money(technicalHour.saleHour)} emphasis />
        </div>
        <p className="field-help">A hora técnica é calculada a partir dos custos mensais, horas disponíveis, percentual faturável e markup. Não existe campo de edição direta.</p>
      </ProfileSection>
      <ProfileSection title="Preços-base e mínimos de S1 a S7">
        <div className="technical-service-grid">
          {Object.entries(technical.services || {}).map(([code, service]: [string, any]) => (
            <article className="technical-service-card" key={code}>
              <h4>{code}</h4>
              {'unit_price' in service ? (
                <Text path={`technical_pricing.services.${code}.unit_price`} label="Preço unitário" value={service.unit_price} set={field} />
              ) : (
                <Text path={`technical_pricing.services.${code}.base_price`} label="Preço-base" value={service.base_price} set={field} />
              )}
              <Text path={`technical_pricing.services.${code}.minimum_price`} label="Preço mínimo" value={service.minimum_price} set={field} />
              {'different_building_addition_rate' in service && (
                <Percent path={`technical_pricing.services.${code}.different_building_addition_rate`} label="Edificação diferente" value={service.different_building_addition_rate} set={field} />
              )}
              {'zurc_opinion_bonus_rate' in service && (
                <Percent path={`technical_pricing.services.${code}.zurc_opinion_bonus_rate`} label="Bonificação parecer ZURC" value={service.zurc_opinion_bonus_rate} set={field} />
              )}
            </article>
          ))}
        </div>
      </ProfileSection>
      <ProfileSection title="Custos mensais e capacidade">
        <EditableRows
          rows={technical.office_costs || []}
          onChange={(rows) => field('technical_pricing.office_costs', rows)}
          columns={[{ key: 'label', label: 'Componente' }, { key: 'monthly_value', label: 'Valor mensal' }]}
          addRow={() => ({ id: `custo_${Date.now()}`, label: 'Novo custo', monthly_value: '0.00' })}
        />
        <div className="form-grid four-columns">
          <Text path="technical_pricing.capacity.available_hours_month" label="Horas disponíveis/mês" value={technical.capacity?.available_hours_month} set={field} />
          <Percent path="technical_pricing.capacity.billable_fraction" label="Percentual faturável" value={technical.capacity?.billable_fraction} set={field} />
          <Percent path="technical_pricing.capacity.hour_markup" label="Markup da hora" value={technical.capacity?.hour_markup} set={field} />
          <Percent path="technical_pricing.capacity.zurc_net_participation" label="Participação líquida da ZURC" value={technical.capacity?.zurc_net_participation} set={field} />
        </div>
      </ProfileSection>
      <ProfileSection title="Parâmetros comerciais técnicos">
        <div className="form-grid four-columns">
          <Text path="technical_pricing.commercial.art_default" label="ART padrão" value={technical.commercial?.art_default} set={field} />
          <Text path="technical_pricing.commercial.additional_meeting_hours" label="Duração da reunião adicional (h)" value={technical.commercial?.additional_meeting_hours} set={field} />
          <Text path="technical_pricing.commercial.additional_visit_price" label="Valor da visita adicional" value={technical.commercial?.additional_visit_price} set={field} />
          <Text path="technical_pricing.commercial.additional_visit_hours" label="Duração da visita adicional (h)" value={technical.commercial?.additional_visit_hours} set={field} />
          <Text path="technical_pricing.commercial.deadline_multipliers.normal" label="Fator prazo normal" value={technical.commercial?.deadline_multipliers?.normal} set={field} />
          <Text path="technical_pricing.commercial.deadline_multipliers.reduced" label="Fator prazo reduzido" value={technical.commercial?.deadline_multipliers?.reduced} set={field} />
          <Text path="technical_pricing.commercial.deadline_multipliers.urgent" label="Fator prazo urgente" value={technical.commercial?.deadline_multipliers?.urgent} set={field} />
          <Text path="technical_pricing.commercial.rounding_unit" label="Unidade técnica de arredondamento" value={technical.commercial?.rounding_unit} set={field} />
          <Text path="technical_pricing.commercial.payment_threshold" label="Limite da sugestão de pagamento" value={technical.commercial?.payment_threshold} set={field} />
          <Percent path="technical_pricing.commercial.manual_increase_alert_rate" label="Alerta de aumento manual" value={technical.commercial?.manual_increase_alert_rate} set={field} />
          <Percent path="technical_pricing.commercial.manual_reduction_alert_rate" label="Alerta de redução manual" value={technical.commercial?.manual_reduction_alert_rate} set={field} />
        </div>
      </ProfileSection>
      <ProfileSection title="Deslocamento e prazos-base">
        <div className="form-grid four-columns">
          <Text path="technical_pricing.travel.home_city" label="Município-base" value={technical.travel?.home_city} set={field} />
          <Text path="technical_pricing.travel.vehicle_consumption_km_l" label="Consumo do veículo (km/L)" value={technical.travel?.vehicle_consumption_km_l} set={field} />
          <Text path="technical_pricing.travel.default_gasoline_brl_l" label="Gasolina padrão (R$/L)" value={technical.travel?.default_gasoline_brl_l} set={field} />
          <Text path="technical_pricing.deadlines.report_business_days" label="Parecer (dias úteis)" value={technical.deadlines?.report_business_days} set={field} type="number" />
          <Text path="technical_pricing.deadlines.small_project_business_days" label="Projeto pequeno (dias úteis)" value={technical.deadlines?.small_project_business_days} set={field} type="number" />
          <Text path="technical_pricing.deadlines.large_project_business_days" label="Projeto grande (dias úteis)" value={technical.deadlines?.large_project_business_days} set={field} type="number" />
        </div>
      </ProfileSection>
      <ProfileSection title="Coeficientes de repetição">
        <div className="form-grid four-columns">
          {Object.entries(technical.repetition?.floor_coefficients || {}).map(([key, values]: [string, any]) => (
            <Text
              key={key}
              path={`technical_pricing.repetition.floor_coefficients.${key}`}
              label={`Pavimentos ${key.replace('_plus', ' ou mais')}`}
              value={(values || []).join('; ')}
              set={(path, value) => field(path, String(value).split(';').map((item) => item.trim()).filter(Boolean))}
            />
          ))}
          <Text
            path="technical_pricing.repetition.reinforcement_coefficients"
            label="Elementos semelhantes"
            value={(technical.repetition?.reinforcement_coefficients || []).join('; ')}
            set={(path, value) => field(path, String(value).split(';').map((item) => item.trim()).filter(Boolean))}
          />
        </div>
      </ProfileSection>
      <ProfileSection title="Padrões de reuniões, revisões e visitas">
        <div className="technical-defaults-table">
          <div className="technical-defaults-head"><span>Serviço</span><span>Reuniões</span><span>Revisões antes</span><span>Revisões depois</span><span>Visitas</span><span>Horas/visita</span></div>
          {Object.entries(technical.service_defaults || {}).map(([code, defaults]: [string, any]) => (
            <div className="technical-defaults-row" key={code}>
              <strong>{code}</strong>
              {(['included_meetings', 'revisions_before', 'revisions_after', 'included_visits', 'included_visit_hours'] as const).map((key) => (
                <input
                  key={key}
                  aria-label={`${code} ${key}`}
                  type="number"
                  step="0.01"
                  value={defaults[key] ?? ''}
                  onChange={(e) => field(`technical_pricing.service_defaults.${code}.${key}`, key === 'included_visit_hours' ? e.target.value : Number(e.target.value))}
                />
              ))}
            </div>
          ))}
        </div>
      </ProfileSection>
      <ProfileSection title="Matrizes técnicas versionadas">
        <p className="field-help">Pesos, faixas, fatores e gatilhos manuais abaixo são parâmetros provisórios de homologação. Alterações criam nova versão e afetam apenas propostas que adotarem essa versão.</p>
        {Object.entries(technical.matrices || {}).map(([matrixKey, matrix]: [string, any]) => (
          <TechnicalMatrixEditor key={matrixKey} matrixKey={matrixKey} matrix={matrix} set={field} />
        ))}
      </ProfileSection>
      <ProfileSection title="Iteração">
        <div className="form-grid three-columns">
          <Text path="iteration.tolerance" label="Tolerância" value={get(form, 'iteration.tolerance')} set={field} />
          <Text
            path="iteration.max_iterations"
            label="Limite de iterações"
            value={get(form, 'iteration.max_iterations')}
            set={field}
            type="number"
          />
          <Text
            path="iteration.non_convergence"
            label="Não convergência"
            value={get(form, 'iteration.non_convergence') || 'erro explícito'}
            set={field}
          />
        </div>
      </ProfileSection>
      <div className="profile-bottom-actions">
        <Field id="duplicate-profile" label="Novo código para duplicação">
          <input id="duplicate-profile" value={duplicateId} onChange={(e) => setDuplicateId(e.target.value)} />
        </Field>
        <Button type="button" variant="secondary" onClick={duplicate}>
          Duplicar perfil
        </Button>
        <Button type="button" variant="ghost" onClick={() => setShowTechnical((v) => !v)}>
          Ver JSON para auditoria
        </Button>
        <Button type="button" variant="ghost" onClick={exportJson}>
          Exportar JSON
        </Button>
        <label className="button secondary file-action">
          Importar configuração
          <input type="file" accept="application/json" onChange={(e) => e.target.files?.[0] && importJson(e.target.files[0])} />
        </label>
      </div>
      {showHistory && (
        <div className="profile-history">
          <h3>Histórico de versões</h3>
          {history.map((x) => (
            <div key={`${x.profile_id}-${x.version}`}>
              <strong>{x.version}</strong>
              <span>
                {x.status} · {x.active ? 'ativo' : 'inativo'} · {x.updated_by} · {x.updated_at}
              </span>
            </div>
          ))}
        </div>
      )}
      {showTechnical && (
        <div className="technical-json">
          <h3>JSON do perfil versionado</h3>
          <p>Somente leitura para auditoria, exportação e rastreabilidade. Os formulários acima são a interface de edição.</p>
          <pre>{JSON.stringify(form, null, 2)}</pre>
        </div>
      )}
    </section>
  );
}
function ProfileSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="profile-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}
function Text({
  path,
  label,
  value,
  set,
  type = 'text',
  multiline = false,
}: {
  path: string;
  label: string;
  value: any;
  set: (p: string, v: any) => void;
  type?: string;
  multiline?: boolean;
}) {
  const id = `profile-${path.replaceAll('.', '-')}`;
  return (
    <Field id={id} label={label}>
      {multiline ? (
        <textarea id={id} rows={5} value={value ?? ''} onChange={(e) => set(path, e.target.value)} />
      ) : (
        <input
          id={id}
          type={type}
          value={value ?? ''}
          onChange={(e) => set(path, type === 'number' ? Number(e.target.value) : e.target.value)}
        />
      )}
    </Field>
  );
}
function Percent({ path, label, value, set }: { path: string; label: string; value: any; set: (p: string, v: any) => void }) {
  const id = `profile-${path.replaceAll('.', '-')}`;
  return (
    <Field id={id} label={`${label} (%)`}>
      <input id={id} inputMode="decimal" value={pct(value)} onChange={(e) => set(path, ratio(e.target.value))} />
    </Field>
  );
}

function money(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
}
function ReadOnly({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className={`field read-only-field${emphasis ? ' emphasis' : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function EditableRows({
  rows,
  columns,
  onChange,
  addRow,
}: {
  rows: any[];
  columns: { key: string; label: string }[];
  onChange: (rows: any[]) => void;
  addRow: () => any;
}) {
  return (
    <div className="editable-rows">
      <div className="editable-rows-head">
        {columns.map((column) => <span key={column.key}>{column.label}</span>)}
        <span>Ação</span>
      </div>
      {rows.map((row, index) => (
        <div className="editable-row" key={row.id || index}>
          {columns.map((column) => (
            <input
              key={column.key}
              aria-label={`${column.label} ${index + 1}`}
              value={row[column.key] ?? ''}
              onChange={(event) => {
                const next = clone(rows);
                next[index] = { ...next[index], [column.key]: event.target.value };
                onChange(next);
              }}
            />
          ))}
          <Button type="button" variant="ghost" onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}>Remover</Button>
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={() => onChange([...rows, addRow()])}>Adicionar componente</Button>
    </div>
  );
}
function TechnicalMatrixEditor({ matrixKey, matrix, set }: { matrixKey: string; matrix: any; set: (path: string, value: any) => void }) {
  const labels: Record<string, string> = {
    superstructure: 'S1 e S2 · Superestrutura',
    shallow_foundation: 'S1 e S3 · Fundação rasa',
    deep_foundation: 'S4 · Fundação profunda',
    visit_report: 'S5 · Parecer técnico',
    reinforcement: 'S6 e S7 · Reforço estrutural',
  };
  return (
    <details className="technical-matrix">
      <summary>{labels[matrixKey] || matrixKey}</summary>
      <div className="matrix-points-grid">
        {Object.entries(matrix.points || {}).map(([key, value]) => (
          <Text key={key} path={`technical_pricing.matrices.${matrixKey}.points.${key}`} label={key.replaceAll('_', ' ')} value={value} set={set} />
        ))}
      </div>
      <h4>Faixas e fatores</h4>
      <div className="matrix-ranges">
        {(matrix.ranges || []).map((range: any, index: number) => (
          <div className="matrix-range-row" key={`${matrixKey}-${index}`}>
            <input aria-label={`${matrixKey} mínimo ${index + 1}`} value={range.min ?? ''} onChange={(event) => set(`technical_pricing.matrices.${matrixKey}.ranges.${index}.min`, event.target.value)} />
            <input aria-label={`${matrixKey} máximo ${index + 1}`} value={range.max ?? ''} placeholder="Sem limite" onChange={(event) => set(`technical_pricing.matrices.${matrixKey}.ranges.${index}.max`, event.target.value || null)} />
            <input aria-label={`${matrixKey} classificação ${index + 1}`} value={range.label ?? ''} onChange={(event) => set(`technical_pricing.matrices.${matrixKey}.ranges.${index}.label`, event.target.value)} />
            <input aria-label={`${matrixKey} fator ${index + 1}`} value={range.factor ?? ''} placeholder="Análise manual" onChange={(event) => set(`technical_pricing.matrices.${matrixKey}.ranges.${index}.factor`, event.target.value || null)} />
          </div>
        ))}
      </div>
      <Text
        path={`technical_pricing.matrices.${matrixKey}.manual_flags`}
        label="Gatilhos manuais (um por linha)"
        value={(matrix.manual_flags || []).join('\n')}
        set={(path, value) => set(path, String(value).split(/\r?\n/).map((item) => item.trim()).filter(Boolean))}
        multiline
      />
    </details>
  );
}
